import { useMyRolesQuery, User, useUserQuery } from 'generated/graphql';
import { getAccessToken } from 'helpers/auth';
import { syncIntercom } from 'helpers/intercom';
import { shouldHideIntercomLauncher } from 'helpers/liveChat';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Boots the Intercom messenger and keeps its launcher out of the way during
 * assessments and wherever the company the candidate is dealing with has
 * "Display live chat to users" switched off.
 *
 * The candidate's roles carry their companies' setting, so one request at
 * sign-in (shared with the role list through the Apollo cache) decides for
 * every page: inside a role's pages the chat follows that role's company,
 * elsewhere it shows while any company still has it on.
 *
 * The boot waits for that decision, so a disabled company's page never shows
 * the launcher even for a moment. Errors fall back to showing the chat, the
 * default.
 */
const IntercomLiveChatGate = () => {
  const { pathname } = useLocation();
  const isLoggedIn = Boolean(getAccessToken());

  // Already fetched by the wrappers above the router, so this reads the cache.
  const { data: userData } = useUserQuery({ skip: !isLoggedIn });

  const { data: rolesData, loading } = useMyRolesQuery({
    skip: !isLoggedIn,
    // The gate never shows the error: unknown roles keep the default.
    onError: () => undefined,
  });

  const launcherHidden = shouldHideIntercomLauncher(
    pathname,
    rolesData?.myRoles
  );

  useEffect(() => {
    if (!userData?.User || loading) {
      return;
    }
    syncIntercom(userData.User as User, launcherHidden);
  }, [userData, loading, launcherHidden]);

  return null;
};

export default IntercomLiveChatGate;
